import { useCallback } from 'react';
import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import { clearCVFormData } from 'utils/cv-form-db';
import { logError } from 'utils/datadog';

/**
 * Clear any locally-stored CV draft (name/email/etc. in IndexedDB) before logging out,
 * then log out. Best-effort: the clear is idempotent and its failure must NEVER block
 * logout. Shared by both logout call sites (the user-initiated modal and the forced
 * logout on token-refresh failure) so the PII wipe can't be forgotten at one of them.
 *
 * The guest flow stores nothing locally, so it needs no equivalent.
 */
export const clearDraftThenLogout = async (
  logout: (options?: LogoutOptions) => Promise<void>
): Promise<void> => {
  try {
    await clearCVFormData();
  } catch (error) {
    logError('Failed to clear CV draft on logout', error);
  }
  await logout({ logoutParams: { returnTo: window.location.origin } });
};

/** Hook form for component call sites: clears the CV draft, then logs out. */
export const useLogout = () => {
  const { logout } = useAuth0();
  return useCallback(() => clearDraftThenLogout(logout), [logout]);
};
